import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { overtimeRecordsService, userService } from '../services/api';
import { Clock, Save, Calendar, User, AlertCircle, CheckCircle, ShieldCheck, Info, Users, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const OvertimeRecordsManagement = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState({});

  const [bulkDate, setBulkDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkMinutes, setBulkMinutes] = useState(60);
  const [selectedBulkRecords, setSelectedBulkRecords] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isBulkMode = selectedUserIds.length > 1;
  const selectedUserId = selectedUserIds.length === 1 ? selectedUserIds[0] : '';

  useEffect(() => {
    const reportId = searchParams.get('reportId');
    const userId = searchParams.get('userId');
    const year = searchParams.get('year');
    const month = searchParams.get('month');

    const loadFromReport = async () => {
      if (reportId) {
        try {
          const response = await overtimeRecordsService.getReportById(reportId);
          if (response.data?.success) {
            const { user_id, ref_year, ref_month } = response.data.data;
            setSelectedUserIds([user_id]);
            setSelectedYear(Number(ref_year));
            setSelectedMonth(Number(ref_month));
            setSearchParams({}, { replace: true });
          }
        } catch (error) {
          console.error('Erro ao carregar relatório:', error);
          toast.error('Erro ao carregar dados da notificação');
        }
      } else if (userId && year && month) {
        setSelectedUserIds([userId]);
        setSelectedYear(Number(year));
        setSelectedMonth(Number(month));
      }
    };

    loadFromReport();
  }, [searchParams, setSearchParams]);

  const { data: usersData } = useQuery({
    queryKey: ['users-for-overtime'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  const { data: reportStatusData, refetch: refetchReport } = useQuery({
    queryKey: ['overtime-report-status', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      selectedUserId
        ? overtimeRecordsService.getMonthlyReport({
            userId: selectedUserId,
            year: selectedYear,
            month: selectedMonth
          })
        : Promise.resolve(null),
    enabled: !!selectedUserId,
  });

  const reportData = reportStatusData?.data?.data;
  const isFullyApproved = reportData?.status === 'APPROVED';

  const { data: overtimeData, isLoading, refetch } = useQuery({
    queryKey: ['overtime-records', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      selectedUserId
        ? overtimeRecordsService.getOvertimeRecords({
            userId: selectedUserId,
            year: selectedYear,
            month: selectedMonth,
          })
        : Promise.resolve({ data: [], meta: {} }),
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  const { data: bulkOvertimeData, refetch: refetchBulkOvertime } = useQuery({
    queryKey: ['overtime-records-bulk', selectedUserIds, selectedYear, selectedMonth],
    queryFn: async () => {
      if (!isBulkMode || selectedUserIds.length === 0) {
        return { data: [] };
      }
      const results = await Promise.all(
        selectedUserIds.map(async (userId) => {
          const [recordsResponse, reportResponse] = await Promise.all([
            overtimeRecordsService.getOvertimeRecords({
              userId,
              year: selectedYear,
              month: selectedMonth,
            }),
            overtimeRecordsService.getMonthlyReport({
              userId,
              year: selectedYear,
              month: selectedMonth,
            })
          ]);
          const user = users.find(u => u.id === userId);
          const reportData = reportResponse.data?.data;
          return {
            userId,
            userName: user?.full_name || 'Unknown',
            records: recordsResponse.data?.data || [],
            reportStatus: reportData?.status || 'PENDING',
            pendingApprovers: reportData?.pending_approvals || [],
            currentApprovals: reportData?.current_approvals || 0,
            totalManagers: reportData?.total_managers || 0,
            approvalsList: reportData?.approvals_list || []
          };
        })
      );
      return { data: results };
    },
    enabled: isBulkMode && selectedUserIds.length > 0,
    staleTime: 0,
  });

  const approveMutation = useMutation({
    mutationFn: () => overtimeRecordsService.approveMonthlyReport({
      userId: selectedUserId,
      year: selectedYear,
      month: selectedMonth,
    }),
    onSuccess: (response) => {
      toast.success(response.data.message || 'Aprovação registrada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['overtime-report-status'] });
      queryClient.invalidateQueries({ queryKey: ['overtime-records-bulk'] });
      refetchReport();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar mês');
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (reports) => overtimeRecordsService.bulkApproveMonthlyReports({ reports }),
    onSuccess: (response) => {
      toast.success(response.data.message || 'Aprovações registradas com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['overtime-report-status'] });
      queryClient.invalidateQueries({ queryKey: ['overtime-records-bulk'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      refetchReport();
      refetchBulkOvertime();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar em lote');
    },
  });

  const saveMutation = useMutation({
    mutationFn: ({ userId, records }) => {
      const recordsArray = Object.entries(records).map(([date, minutes]) => ({
        date,
        minutes: Number(minutes || 0),
      }));
      return overtimeRecordsService.bulkUpsertOvertimeRecords(userId, { records: recordsArray });
    },
    onSuccess: async () => {
      toast.success('Horas extras salvas com sucesso!');
      await queryClient.invalidateQueries({ queryKey: ['overtime-records'] });
      await queryClient.invalidateQueries({ queryKey: ['payslips'] });
      await queryClient.invalidateQueries({ queryKey: ['overtime-report-status'] });
      setTimeout(async () => {
        try {
          const updatedData = await refetch();
          if (updatedData.data) {
            let recordsArray = [];
            if (updatedData.data.data?.data && Array.isArray(updatedData.data.data.data)) {
              recordsArray = updatedData.data.data.data;
            } else if (updatedData.data.data && Array.isArray(updatedData.data.data)) {
              recordsArray = updatedData.data.data;
            } else if (updatedData.data && Array.isArray(updatedData.data)) {
              recordsArray = updatedData.data;
            }
            if (recordsArray.length > 0) {
              const recordsMap = {};
              recordsArray.forEach((record) => {
                const dateStr = record.date instanceof Date
                  ? record.date.toISOString().split('T')[0]
                  : String(record.date).split('T')[0];
                recordsMap[dateStr] = record.minutes || 0;
              });
              setRecords(recordsMap);
            } else {
              setRecords({});
            }
          }
        } catch (error) {
          console.error('Erro ao recarregar dados:', error);
        }
      }, 500);
    },
    onError: (error) => {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar horas extras');
    },
  });

  const bulkSaveMutation = useMutation({
    mutationFn: (data) => overtimeRecordsService.bulkCreateMultipleUsers(data),
    onSuccess: (response) => {
      toast.success(response.data.message || 'Horas extras salvas em lote!');
      setBulkMinutes(60);
      queryClient.invalidateQueries({ queryKey: ['overtime-records'] });
      queryClient.invalidateQueries({ queryKey: ['overtime-records-bulk'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      refetchBulkOvertime();
    },
    onError: (error) => {
      console.error('Erro ao salvar em lote:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar horas extras em lote');
    },
  });

  const deleteBulkRecordsMutation = useMutation({
    mutationFn: (recordsToDelete) => overtimeRecordsService.deleteBulkOvertimeRecords({ records: recordsToDelete }),
    onSuccess: (response) => {
      toast.success(response.data.message || 'Registros deletados com sucesso!');
      setSelectedBulkRecords([]);
      queryClient.invalidateQueries({ queryKey: ['overtime-records'] });
      queryClient.invalidateQueries({ queryKey: ['overtime-records-bulk'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
    onError: (error) => {
      console.error('Erro ao deletar:', error);
      toast.error(error.response?.data?.message || 'Erro ao deletar registros');
    },
  });

  useEffect(() => {
    if (saveMutation.isLoading) return;
    if (!isLoading && overtimeData) {
      let recordsArray = [];
      if (overtimeData.data?.data && Array.isArray(overtimeData.data.data)) {
        recordsArray = overtimeData.data.data;
      } else if (overtimeData.data && Array.isArray(overtimeData.data)) {
        recordsArray = overtimeData.data;
      } else if (Array.isArray(overtimeData)) {
        recordsArray = overtimeData;
      }
      if (recordsArray.length > 0) {
        const recordsMap = {};
        recordsArray.forEach((record) => {
          const dateStr = record.date instanceof Date
            ? record.date.toISOString().split('T')[0]
            : String(record.date).split('T')[0];
          recordsMap[dateStr] = record.minutes || 0;
        });
        setRecords(recordsMap);
      } else {
        setRecords({});
      }
    }
  }, [overtimeData, isLoading, saveMutation.isLoading]);

  useEffect(() => {
    setRecords({});
  }, [selectedUserId, selectedYear, selectedMonth]);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  const daysInMonth = useMemo(() => {
    const days = [];
    const daysCount = new Date(selectedYear, selectedMonth, 0).getDate();
    for (let day = 1; day <= daysCount; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        date: dateStr,
        dayOfWeek: date.getDay(),
        dayName: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getDay()],
      });
    }
    return days;
  }, [selectedYear, selectedMonth]);

  const handleMinutesChange = (date, value) => {
    const minutes = value === '' ? 0 : Math.max(0, Number(value));
    setRecords((prev) => ({
      ...prev,
      [date]: minutes,
    }));
  };

  const handleSave = () => {
    if (selectedUserIds.length !== 1) {
      toast.error('Selecione exatamente um funcionário para salvar');
      return;
    }
    saveMutation.mutate({
      userId: selectedUserId,
      records,
    });
  };

  const formatTime = (minutes) => {
    if (!minutes || minutes === 0) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  const formatDateLocal = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = String(dateStr).split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const totalMinutes = useMemo(() => {
    return Object.values(records).reduce((sum, minutes) => sum + Number(minutes || 0), 0);
  }, [records]);

  const selectedUser = users.find((u) => u.id === Number(selectedUserId));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
          Controle de Horas Extras
        </h1>
        <p className="text-gray-600 mt-1 text-sm sm:text-base">
          Registre e aprove as horas extras dos funcionários
        </p>
      </div>

      {/* Card de Instruções - oculto no mobile */}
      <div className="hidden sm:block bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">Como usar:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium">1. Selecione funcionário(s)</p>
                <p className="text-xs opacity-80">1=individual, vários=lote</p>
              </div>
              <div>
                <p className="font-medium">2. Escolha Mês/Ano</p>
              </div>
              <div>
                <p className="font-medium">3. Cadastre horas extras</p>
                <p className="text-xs opacity-80">Tabela (individual) ou Data+Min (lote)</p>
              </div>
              <div>
                <p className="font-medium">4. Aprovar registros</p>
                <p className="text-xs opacity-80">Necessário para constar no holerite</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 mb-4">
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            <User className="w-3.5 h-3.5 inline mr-1" />
            Selecione funcionário(s)
          </label>
          <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto bg-gray-50">
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-1">
              {users.map((user) => (
                <label
                  key={user.id}
                  className={`flex items-center p-1.5 rounded cursor-pointer transition-colors text-xs ${
                    selectedUserIds.includes(user.id)
                      ? 'bg-blue-100 border border-blue-300'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUserIds([...selectedUserIds, user.id]);
                      } else {
                        setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                      }
                    }}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-1.5 text-gray-700 truncate" title={user.full_name}>
                    {user.full_name}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {selectedUserIds.length === 0
              ? 'Nenhum selecionado'
              : selectedUserIds.length === 1
              ? '1 func. - individual'
              : `${selectedUserIds.length} func. - lote`}
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar className="w-3.5 h-3.5 inline mr-0.5" />
              Mês
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>{name.substring(0, 3)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ano</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              min="2020"
              max="2100"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Modo Individual */}
      {selectedUserIds.length === 1 && (
        <>
          <div className="bg-white rounded-lg shadow mb-4">
            <div className="p-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{selectedUser?.full_name}</p>
                <p className="text-xs text-gray-600">{monthNames[selectedMonth - 1]}/{selectedYear}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-600">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-blue-600">{formatTime(totalMinutes)}</p>
              </div>
            </div>

            {/* Mobile: Card layout */}
            <div className="sm:hidden divide-y divide-gray-100">
              {daysInMonth.map((dayInfo) => {
                const minutes = records[dayInfo.date] || 0;
                const isWeekend = dayInfo.dayOfWeek === 0 || dayInfo.dayOfWeek === 6;
                return (
                  <div key={dayInfo.date} className={`p-1.5 flex items-center justify-between gap-1 ${isWeekend ? 'bg-gray-50' : ''}`}>
                    <div className="flex items-center gap-1 min-w-0">
                      <span className={`inline-flex px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                        isWeekend ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {dayInfo.dayName.substring(0, 3)}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {String(dayInfo.day).padStart(2, '0')}/{String(selectedMonth).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={minutes || ''}
                        onChange={(e) => handleMinutesChange(dayInfo.date, e.target.value)}
                        placeholder="0"
                        className="w-12 px-1 py-0.5 text-[10px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="text-[10px] text-gray-500 w-8 text-right">
                        {formatTime(minutes) || '-'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dia</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Minutos</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Horas</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {daysInMonth.map((dayInfo) => {
                    const minutes = records[dayInfo.date] || 0;
                    const isWeekend = dayInfo.dayOfWeek === 0 || dayInfo.dayOfWeek === 6;
                    return (
                      <tr key={dayInfo.date} className={isWeekend ? 'bg-gray-50' : ''}>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          {String(dayInfo.day).padStart(2, '0')}/{String(selectedMonth).padStart(2, '0')}/{selectedYear}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            isWeekend ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {dayInfo.dayName}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={minutes || ''}
                            onChange={(e) => handleMinutesChange(dayInfo.date, e.target.value)}
                            placeholder="0"
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600">
                          {formatTime(minutes) || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
                <AlertCircle className="w-4 h-4" />
                <span>Horas calculadas automaticamente no holerite</span>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSave}
                  disabled={saveMutation.isLoading}
                  className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isLoading || isFullyApproved}
                  className={`flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm rounded-lg flex items-center justify-center gap-1.5 text-white ${
                    isFullyApproved
                      ? 'bg-green-600 cursor-default'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } disabled:opacity-50`}
                >
                  {isFullyApproved ? <CheckCircle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  {isFullyApproved ? 'Aprovado' : 'Aprovar'}
                </button>
              </div>
            </div>
          </div>

          {reportData && (
            <div className={`p-2 sm:p-3 rounded-lg border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 ${
              isFullyApproved ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center gap-2">
                {isFullyApproved ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                )}
                <span className="text-sm">
                  {isFullyApproved ? 'Totalmente Aprovado' : 'Aguardando aprovações'}
                  {' - '}{reportData.current_approvals}/{reportData.total_managers} approvals
                </span>
              </div>
              {reportData.approvals_list?.length > 0 && (
                <div className="text-xs text-right">
                  {reportData.approvals_list.map((a, i) => (
                    <p key={i}>{a.name} em {formatDateLocal(a.date)}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modo Lote */}
      {isBulkMode && (
        <div className="space-y-4">
          {/* Card de cadastro em lote */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-green-600" />
              <p className="font-semibold text-gray-900 text-sm">Cadastro em Lote</p>
              <span className="text-xs text-gray-600">({selectedUserIds.length} func.)</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Data</label>
                <input
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Minutos</label>
                <input
                  type="number"
                  min="0"
                  step="15"
                  value={bulkMinutes}
                  onChange={(e) => setBulkMinutes(Number(e.target.value) || 0)}
                  className="w-full px-2 py-1.5 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div className="col-span-2 sm:col-span-1 flex items-end">
                <button
                  onClick={() => {
                    if (!bulkDate) {
                      toast.error('Selecione a data');
                      return;
                    }
                    if (bulkMinutes <= 0) {
                      toast.error('Informe os minutos');
                      return;
                    }
                    bulkSaveMutation.mutate({
                      records: selectedUserIds.map(userId => ({
                        userId,
                        date: bulkDate,
                        minutes: bulkMinutes
                      }))
                    });
                  }}
                  disabled={bulkSaveMutation.isLoading}
                  className="w-full px-3 py-1.5 text-xs sm:text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  {bulkSaveMutation.isLoading ? 'Salvando...' : `Salvar (${selectedUserIds.length})`}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-600">
              A mesma quantidade de minutos será salva para todos os funcionários selecionados na data informada.
            </p>
          </div>

          {/* Card de aprovação em lote */}
          <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Aprovação em Lote</p>
                <p className="text-xs text-gray-500">{monthNames[selectedMonth - 1]}/{selectedYear} · {selectedUserIds.length} func.</p>
              </div>
            </div>

            {bulkOvertimeData?.data && bulkOvertimeData.data.length > 0 && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-medium text-gray-500">{bulkOvertimeData.data.length} registros encontrados</p>
                  {selectedBulkRecords.length > 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleteBulkRecordsMutation.isLoading}
                        className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1 border border-red-200"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span className="hidden sm:inline">Deletar</span>({selectedBulkRecords.length})
                      </button>
                      <button
                        onClick={() => {
                          bulkApproveMutation.mutate(
                            selectedBulkRecords.map(r => ({
                              userId: r.userId,
                              year: selectedYear,
                              month: selectedMonth
                            }))
                          );
                        }}
                        disabled={bulkApproveMutation.isLoading}
                        className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        <ShieldCheck className="w-3 h-3" />
                        <span className="hidden sm:inline">Aprovar</span>({selectedBulkRecords.length})
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                  {bulkOvertimeData.data.map((userData) => {
                    const total = userData.records.reduce((sum, r) => sum + (r.minutes || 0), 0);
                    const isApproved = userData.reportStatus === 'APPROVED';
                    return (
                      <div key={userData.userId} className={`rounded-lg p-3 border ${
                        isApproved ? 'bg-green-50 border-green-200' : 'bg-white border-indigo-100'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900 text-sm truncate">{userData.userName}</p>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                isApproved
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {isApproved ? 'Aprovado' : 'Pendente'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {formatTime(total)} · {userData.records.length} dia(s)
                            </p>
                          </div>
                          {!isApproved && (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                              userData.pendingApprovers.length > 0
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {userData.pendingApprovers.length > 0 ? `${userData.pendingApprovers.length} pendente(s)` : 'Pronto'}
                            </span>
                          )}
                        </div>

                        {!isApproved && userData.pendingApprovers.length > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-700">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span>Aguardando: </span>
                            <span className="font-medium">{userData.pendingApprovers.map(m => m.name.split(' ')[0]).join(', ')}</span>
                          </div>
                        )}

                        {!isApproved && userData.records.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {userData.records.map((r) => {
                              const isSelected = selectedBulkRecords.some(
                                sel => sel.userId === userData.userId && sel.date === r.date
                              );
                              return (
                                <span
                                  key={r.date}
                                  onClick={() => {
                                    setSelectedBulkRecords(prev => {
                                      if (isSelected) {
                                        return prev.filter(
                                          sel => !(sel.userId === userData.userId && sel.date === r.date)
                                        );
                                      } else {
                                        return [...prev, { userId: userData.userId, date: r.date }];
                                      }
                                    });
                                  }}
                                  className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer flex items-center gap-0.5 ${
                                    isSelected
                                      ? 'bg-indigo-500 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {isSelected && <CheckCircle className="w-2.5 h-2.5" />}
                                  {formatDateLocal(r.date).slice(0,5)}: {r.minutes}m
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {isApproved && userData.approvalsList.length > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-green-700">
                            <CheckCircle className="w-3 h-3" />
                            <span>{userData.approvalsList.map(a => a.name.split(' ')[0]).join(', ')}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedBulkRecords.length > 0 && (
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pb-2 border-b border-gray-200">
                    <span>{selectedBulkRecords.length} registro(s) selecionado(s)</span>
                    <button
                      onClick={() => setSelectedBulkRecords([])}
                      className="hover:text-gray-700 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Limpar
                    </button>
                  </div>
                )}
              </>
            )}

            <button
              onClick={() => {
                bulkApproveMutation.mutate(
                  selectedUserIds.map(userId => ({
                    userId,
                    year: selectedYear,
                    month: selectedMonth
                  }))
                );
              }}
              disabled={bulkApproveMutation.isLoading}
              className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
            >
              <ShieldCheck className="w-4 h-4" />
              {bulkApproveMutation.isLoading ? 'Aprovando...' : `Aprovar Mês para ${selectedUserIds.length} funcionário${selectedUserIds.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {selectedUserIds.length === 0 && (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-2">Nenhum funcionário selecionado</p>
          <p className="text-sm">Selecione um ou mais funcionários acima para começar</p>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Confirmar exclusão</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Deseja deletar {selectedBulkRecords.length} registro(s) selecionado(s)?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  deleteBulkRecordsMutation.mutate(selectedBulkRecords);
                  setShowDeleteConfirm(false);
                }}
                disabled={deleteBulkRecordsMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteBulkRecordsMutation.isLoading ? 'Deletando...' : 'Deletar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OvertimeRecordsManagement;
